import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, LinearProgress, Chip,
  Paper, FormControl, InputLabel, Select, MenuItem, TextField,
  IconButton, Collapse, Divider, Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  Memory as CorePlatformIcon,
  Hub as ThirdPartyIcon,
  Visibility as ObservabilityIcon,
  Search as SearchIcon,
  FolderOpen as GroupIcon,
  CheckCircle as DoneIcon,
  PlayCircle as ActiveIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { get } from '../../services/apiService';

const PRIMARY = '#1A2E22';
const MUTED = '#5f6368';
const SURFACE = '#F8F9FA';

const PILLAR_META = {
  corePlatform:  { label: 'Core Platform & Runtime Health',      icon: CorePlatformIcon,  color: '#1d6fd4' },
  thirdParty:    { label: 'Third-Party Integration Reliability',  icon: ThirdPartyIcon,    color: '#e53935' },
  observability: { label: 'Safe Delivery',                        icon: ObservabilityIcon, color: '#f4511e' },
};

const DONE_STATES  = ['Closed', 'Done', 'Completed'];
const ACTIVE_STATES = ['Active', 'In Progress'];

function getStateColor(state) {
  if (DONE_STATES.includes(state))  return 'success';
  if (ACTIVE_STATES.includes(state)) return 'primary';
  if (state === 'Removed') return 'default';
  return 'warning';
}

function getStateIcon(state) {
  if (DONE_STATES.includes(state))  return <DoneIcon sx={{ fontSize: 14 }} />;
  if (ACTIVE_STATES.includes(state)) return <ActiveIcon sx={{ fontSize: 14 }} />;
  return null;
}

const UNTAGGED_INITIATIVE = '__untagged__';
const RELIABILITY_CAT_PREFIX = 'reliability-category-';

// ── Mini donut ────────────────────────────────────────────────────────────────
function MiniDonut({ value = 0, size = 36 }) {
  const v = Math.min(Math.max(Number(value), 0), 100);
  const color = v === 100 ? '#2e7d32' : v >= 67 ? '#4caf50' : v >= 33 ? '#ff9800' : '#f44336';
  const r = (size / 2) - 3.5, stroke = 3, circ = 2 * Math.PI * r;
  const dash = (v / 100) * circ;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width={size} height={size} style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`${color}25`} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <Typography sx={{ fontSize: size === 36 ? '0.6rem' : '0.55rem', fontWeight: 700, color, lineHeight: 1 }}>
        {Math.round(v)}%
      </Typography>
    </Box>
  );
}

// ── SP badge ─────────────────────────────────────────────────────────────────
const SpBadge = ({ value }) => value ? (
  <Tooltip title="Story Points">
    <Box sx={{
      px: 1, py: 0.25, borderRadius: '6px',
      bgcolor: 'rgba(0,0,0,0.06)', display: 'inline-flex', alignItems: 'center', gap: 0.25
    }}>
      <Typography variant="caption" fontWeight={700} color="#1a1a1a" lineHeight={1}>{value}</Typography>
      <Typography variant="caption" color={MUTED} lineHeight={1} sx={{ fontSize: '0.65rem' }}>pts</Typography>
    </Box>
  </Tooltip>
) : null;

const PRIORITY_META = {
  1: { label: 'P1', color: '#c62828', bg: '#ffebee' },
  2: { label: 'P2', color: '#e65100', bg: '#fff3e0' },
  3: { label: 'P3', color: '#f57f17', bg: '#fffde7' },
  4: { label: 'P4', color: '#546e7a', bg: '#eceff1' },
};

const PriorityBadge = ({ value }) => {
  const meta = PRIORITY_META[value];
  if (!meta) return null;
  return (
    <Tooltip title={`Priority ${value}`}>
      <Box sx={{
        px: 0.75, py: 0.15, borderRadius: '5px',
        bgcolor: meta.bg, display: 'inline-flex', alignItems: 'center', flexShrink: 0
      }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: meta.color, lineHeight: 1.4 }}>
          {meta.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }
};

// ── Children list ─────────────────────────────────────────────────────────────
const ChildrenList = ({ children, pillarColor }) => {
  if (!children.length) return (
    <Box px={3} py={1.5}>
      <Typography variant="body2" color={MUTED}>No backlog items found.</Typography>
    </Box>
  );
  return (
    <Box sx={{ bgcolor: 'rgba(0,0,0,0.015)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      {children.map((child, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 3, py: 1.25,
            pl: 6,
            borderBottom: i < children.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: pillarColor, flexShrink: 0, opacity: 0.5 }} />
          <Typography variant="body2" color="#2a2a2a" flex={1} minWidth={0} noWrap>{child.title}</Typography>
          <Chip
            label={child.state}
            size="small"
            color={getStateColor(child.state)}
            variant="outlined"
            icon={getStateIcon(child.state)}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          {child.storyPoints && <SpBadge value={child.storyPoints} />}
          {child.assignedTo && (
            <Typography variant="caption" color={MUTED} noWrap sx={{ maxWidth: 120, flexShrink: 0 }}>
              {child.assignedTo.split('<')[0].trim()}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

// ── Feature row ───────────────────────────────────────────────────────────────
const FeatureRow = ({
  feature, pillarColor, isExpanded, isLoadingChildren,
  children, onToggle, isLast
}) => {
  const visibleTags = (feature.tags || [])
    .flatMap(t => t.split(';').map(s => s.trim()).filter(Boolean))
    .filter(t => !t.toLowerCase().startsWith(RELIABILITY_CAT_PREFIX))
    .slice(0, 3);

  return (
    <Box>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          px: 2.5, py: 1.75, cursor: 'pointer',
          borderBottom: (!isExpanded && !isLast) ? '1px solid rgba(0,0,0,0.05)' : 'none',
          '&:hover': { bgcolor: `${pillarColor}06` },
          transition: 'background 0.15s'
        }}
      >
        {/* Expand icon */}
        <IconButton size="small" sx={{ color: MUTED, flexShrink: 0, p: 0.25 }}>
          {isExpanded
            ? <ExpandLessIcon fontSize="small" />
            : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        {/* Donut */}
        <MiniDonut value={feature.completionRate} />

        {/* Title + tags */}
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" fontWeight={600} color="#1a1a1a" mb={visibleTags.length ? 0.25 : 0}>
            {feature.title}
          </Typography>
          {visibleTags.length > 0 && (
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {visibleTags.map(tag => (
                <Chip
                  key={tag} label={tag} size="small"
                  sx={{ height: 18, fontSize: '0.68rem', fontWeight: 500, bgcolor: `${pillarColor}10`, color: pillarColor }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Priority */}
        <PriorityBadge value={feature.priority} />

        {/* State */}
        <Chip
          label={feature.state}
          size="small"
          color={getStateColor(feature.state)}
          variant="outlined"
          icon={getStateIcon(feature.state)}
          sx={{ flexShrink: 0, fontSize: '0.72rem', minWidth: 90, justifyContent: 'center' }}
        />

        {/* Story points */}
        {feature.storyPoints > 0 && <SpBadge value={feature.storyPoints} />}

        {/* PBI count badge */}
        {feature.backlogItemCount > 0 && (
          <Tooltip title={`${feature.backlogItemCount} backlog item${feature.backlogItemCount !== 1 ? 's' : ''}`}>
            <Box sx={{
              px: 1, py: 0.25, borderRadius: '6px',
              bgcolor: `${pillarColor}15`, display: 'inline-flex', alignItems: 'center', gap: 0.25, flexShrink: 0
            }}>
              <Typography variant="caption" fontWeight={700} color={pillarColor} lineHeight={1}>
                {feature.backlogItemCount}
              </Typography>
              <Typography variant="caption" color={pillarColor} lineHeight={1} sx={{ fontSize: '0.65rem' }}>PBIs</Typography>
            </Box>
          </Tooltip>
        )}

        {/* Open in ADO */}
        {feature.adoUrl && (
          <Tooltip title="Open in Azure DevOps">
            <IconButton
              size="small"
              component="a"
              href={feature.adoUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              sx={{ color: MUTED, flexShrink: 0, p: 0.25, '&:hover': { color: pillarColor } }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Children */}
      <Collapse in={isExpanded}>
        {isLoadingChildren ? (
          <Box px={3} py={1.5} display="flex" alignItems="center" gap={1}>
            <CircularProgress size={14} sx={{ color: pillarColor }} />
            <Typography variant="caption" color={MUTED}>Loading…</Typography>
          </Box>
        ) : (
          <ChildrenList children={children || []} pillarColor={pillarColor} />
        )}
      </Collapse>
    </Box>
  );
};

// ── Sub-category group ────────────────────────────────────────────────────────
const InitiativeGroup = ({
  groupKey, displayName, features, pillarColor, pillarKey,
  subCatByPillar, expandedFeatures, featureChildren,
  loadingChildren, onToggleFeature, isOpen, onToggle
}) => {
  const open = isOpen;
  const doneCount = features.filter(f => DONE_STATES.includes(f.state)).length;
  const progress = features.length ? Math.round((doneCount / features.length) * 100) : 0;
  const isGeneral = groupKey === UNTAGGED_INITIATIVE;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Group header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.25, cursor: 'pointer', borderRadius: '10px',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
          transition: 'background 0.15s'
        }}
      >
        <GroupIcon sx={{ fontSize: 16, color: isGeneral ? MUTED : pillarColor, flexShrink: 0 }} />
        <Typography variant="body2" fontWeight={700} color={isGeneral ? MUTED : '#1a1a1a'} flex={1}>
          {displayName}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} minWidth={160} flexShrink={0}>
          <LinearProgress
            variant="determinate" value={progress}
            sx={{
              flex: 1, height: 4, borderRadius: 2,
              bgcolor: `${pillarColor}18`,
              '& .MuiLinearProgress-bar': { bgcolor: pillarColor, borderRadius: 2 }
            }}
          />
          <Typography variant="caption" fontWeight={700} color={pillarColor} minWidth={34}>
            {progress}%
          </Typography>
        </Box>
        <Chip
          label={`${doneCount}/${features.length}`}
          size="small"
          sx={{ height: 20, fontSize: '0.72rem', fontWeight: 600, bgcolor: `${pillarColor}12`, color: pillarColor, flexShrink: 0 }}
        />
        <IconButton size="small" sx={{ p: 0.25, color: MUTED }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Feature rows */}
      <Collapse in={open}>
        <Paper
          elevation={0}
          sx={{
            mx: 1, mb: 0.5,
            borderRadius: '12px',
            border: `1.5px solid ${pillarColor}20`,
            overflow: 'hidden'
          }}
        >
          {features.map((feature, idx) => (
            <FeatureRow
              key={feature.id}
              feature={feature}
              pillarColor={pillarColor}
              isExpanded={expandedFeatures.has(feature.id)}
              isLoadingChildren={loadingChildren.has(feature.id)}
              children={featureChildren[feature.id]}
              onToggle={() => onToggleFeature(feature.id)}
              isLast={idx === features.length - 1}
            />
          ))}
        </Paper>
      </Collapse>
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ReliabilityProductEngineering = ({ initialFilter }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [pillarFilter, setPillarFilter] = useState(initialFilter || '');
  const [searchText, setSearchText] = useState('');
  const [expandedPillars, setExpandedPillars] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [featureChildren, setFeatureChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(new Set());

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (initialFilter !== undefined) setPillarFilter(initialFilter || ''); }, [initialFilter]);

  const loadData = async () => {
    try {
      setLoading(true); setError(null);
      const [featRes, subRes] = await Promise.all([
        get('/api/reliability/features'),
        get('/api/reliability/subcategories')
      ]);
      if (featRes.success) setFeatures(featRes.data || []);
      else setError('Failed to load features');
      if (subRes.success) setSubCategories(subRes.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const subCatByPillar = useMemo(() => {
    const map = {};
    for (const s of subCategories) {
      if (!map[s.pillarKey]) map[s.pillarKey] = {};
      map[s.pillarKey][s.alias] = s.name;
    }
    return map;
  }, [subCategories]);

  const filteredFeatures = useMemo(() => features.filter(f => {
    if (pillarFilter && f.pillarKey !== pillarFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!f.title?.toLowerCase().includes(q) && !f.tags?.some(t => t.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [features, pillarFilter, searchText]);

  const grouped = useMemo(() => {
    const result = {};
    for (const f of filteredFeatures) {
      if (!result[f.pillarKey]) result[f.pillarKey] = {};
      const flatTags = (f.tags || [])
        .flatMap(t => t.split(';').map(s => s.trim().toLowerCase()))
        .filter(Boolean);
      const pillarSubs = subCatByPillar[f.pillarKey] || {};
      const matchedAlias = flatTags
        .filter(t => t.startsWith(RELIABILITY_CAT_PREFIX))
        .map(t => t.slice(RELIABILITY_CAT_PREFIX.length))
        .find(alias => pillarSubs[alias]);
      const groupKey = matchedAlias || UNTAGGED_INITIATIVE;
      if (!result[f.pillarKey][groupKey]) result[f.pillarKey][groupKey] = [];
      result[f.pillarKey][groupKey].push(f);
    }
    return result;
  }, [filteredFeatures, subCatByPillar]);

  const togglePillar = k => setExpandedPillars(p => ({ ...p, [k]: p[k] !== false ? false : true }));

  const isGroupOpen = (pk, gk) => expandedGroups[`${pk}/${gk}`] !== false;
  const toggleGroup = (pk, gk) => {
    const key = `${pk}/${gk}`;
    setExpandedGroups(p => ({ ...p, [key]: p[key] === false ? true : false }));
  };

  const expandAll = () => {
    const pillars = {};
    const groups = {};
    Object.keys(grouped).forEach(pk => {
      pillars[pk] = true;
      Object.keys(grouped[pk] || {}).forEach(gk => { groups[`${pk}/${gk}`] = true; });
    });
    setExpandedPillars(pillars);
    setExpandedGroups(groups);
  };

  const collapseAll = () => {
    const pillars = {};
    const groups = {};
    Object.keys(grouped).forEach(pk => {
      pillars[pk] = false;
      Object.keys(grouped[pk] || {}).forEach(gk => { groups[`${pk}/${gk}`] = false; });
    });
    setExpandedPillars(pillars);
    setExpandedGroups(groups);
  };

  const toggleFeature = async featureId => {
    const next = new Set(expandedFeatures);
    if (next.has(featureId)) {
      next.delete(featureId);
    } else {
      next.add(featureId);
      if (!featureChildren[featureId]) {
        setLoadingChildren(p => new Set(p).add(featureId));
        try {
          const res = await get(`/api/reliability/features/${featureId}/children`);
          if (res.success) setFeatureChildren(p => ({ ...p, [featureId]: res.data || [] }));
        } catch (_) {}
        finally {
          setLoadingChildren(p => { const s = new Set(p); s.delete(featureId); return s; });
        }
      }
    }
    setExpandedFeatures(next);
  };

  const calcProgress = feats => {
    if (!feats.length) return 0;
    const done = feats.filter(f => DONE_STATES.includes(f.state)).length;
    return Math.round((done / feats.length) * 100);
  };

  const pillarKeys = Object.keys(PILLAR_META).filter(k => grouped[k]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );

  return (
    <Box>
      {/* ── Toolbar ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.07)', bgcolor: SURFACE }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small" placeholder="Search features or tags…"
            value={searchText} onChange={e => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: MUTED, mr: 0.5, fontSize: 18 }} /> }}
            sx={{ minWidth: 240, ...inputSx }}
          />
          <FormControl size="small" sx={{ minWidth: 220, ...inputSx }}>
            <InputLabel>Filter by pillar</InputLabel>
            <Select value={pillarFilter} onChange={e => setPillarFilter(e.target.value)} label="Filter by pillar">
              <MenuItem value="">All Pillars</MenuItem>
              {Object.entries(PILLAR_META).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: v.color, flexShrink: 0 }} />
                    {v.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${filteredFeatures.length} feature${filteredFeatures.length !== 1 ? 's' : ''}`}
              size="small" sx={{ bgcolor: 'white', fontWeight: 600 }}
            />
            <Tooltip title="Expand all pillars and groups">
              <IconButton size="small" onClick={expandAll} sx={{ color: MUTED, '&:hover': { color: PRIMARY } }}>
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse all pillars and groups">
              <IconButton size="small" onClick={collapseAll} sx={{ color: MUTED, '&:hover': { color: PRIMARY } }}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && pillarKeys.length === 0 && (
        <Alert severity="info">No features found. Configure Epic IDs in Admin → Reliability Program.</Alert>
      )}

      {/* ── Pillar sections ── */}
      {pillarKeys.map(pillarKey => {
        const meta = PILLAR_META[pillarKey];
        const Icon = meta.icon;
        const pillarFeatures = filteredFeatures.filter(f => f.pillarKey === pillarKey);
        const doneCount = pillarFeatures.filter(f => DONE_STATES.includes(f.state)).length;
        const pillarProgress = calcProgress(pillarFeatures);
        const isPillarOpen = expandedPillars[pillarKey] !== false;
        const initiatives = grouped[pillarKey] || {};

        return (
          <Paper key={pillarKey} elevation={0} sx={{ mb: 3, borderRadius: '20px', overflow: 'hidden', border: '1.5px solid rgba(0,0,0,0.07)' }}>

            {/* ── Pillar header ── */}
            <Box
              role="button" tabIndex={0} aria-expanded={isPillarOpen}
              onClick={() => togglePillar(pillarKey)}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && togglePillar(pillarKey)}
              sx={{
                px: 3, py: 2.5, cursor: 'pointer',
                bgcolor: `${meta.color}0c`,
                borderBottom: isPillarOpen ? `1px solid ${meta.color}20` : 'none',
                display: 'flex', alignItems: 'center', gap: 2,
                transition: 'background 0.15s',
                '&:hover': { bgcolor: `${meta.color}15` }
              }}
            >
              <Box sx={{
                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                bgcolor: `${meta.color}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon sx={{ color: meta.color, fontSize: 24 }} />
              </Box>

              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight={700} color="#1a1a1a" lineHeight={1.3}>
                  {meta.label}
                </Typography>
                <Box display="flex" alignItems="center" gap={1.5} mt={0.75}>
                  <LinearProgress
                    variant="determinate" value={pillarProgress}
                    sx={{
                      flex: 1, maxWidth: 260, height: 6, borderRadius: 3,
                      bgcolor: `${meta.color}20`,
                      '& .MuiLinearProgress-bar': { bgcolor: meta.color, borderRadius: 3 }
                    }}
                  />
                  <Typography variant="caption" fontWeight={700} color={meta.color}>
                    {pillarProgress}%
                  </Typography>
                  <Typography variant="caption" color={MUTED}>
                    {doneCount} of {pillarFeatures.length} features done
                  </Typography>
                </Box>
              </Box>

              <IconButton size="small" aria-label={isPillarOpen ? 'Collapse' : 'Expand'} sx={{ color: MUTED }}>
                {isPillarOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* ── Groups + features ── */}
            <Collapse in={isPillarOpen}>
              <Box sx={{ px: 1.5, py: 1.5 }}>
                {Object.entries(initiatives)
                  .sort(([a], [b]) => {
                    if (a === UNTAGGED_INITIATIVE) return 1;
                    if (b === UNTAGGED_INITIATIVE) return -1;
                    return (subCatByPillar[pillarKey]?.[a] || a).localeCompare(subCatByPillar[pillarKey]?.[b] || b);
                  })
                  .map(([groupKey, groupFeatures]) => {
                    const displayName = groupKey === UNTAGGED_INITIATIVE
                      ? 'General'
                      : (subCatByPillar[pillarKey]?.[groupKey] || groupKey);
                    return (
                      <InitiativeGroup
                        key={groupKey}
                        groupKey={groupKey}
                        displayName={displayName}
                        features={groupFeatures}
                        pillarColor={meta.color}
                        pillarKey={pillarKey}
                        subCatByPillar={subCatByPillar}
                        expandedFeatures={expandedFeatures}
                        featureChildren={featureChildren}
                        loadingChildren={loadingChildren}
                        onToggleFeature={toggleFeature}
                        isOpen={isGroupOpen(pillarKey, groupKey)}
                        onToggle={() => toggleGroup(pillarKey, groupKey)}
                      />
                    );
                  })}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
};

export default ReliabilityProductEngineering;
